import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Invitation } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Copy, Check } from 'lucide-react'
import { useState } from 'react'

const inviteSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type InviteFormData = z.infer<typeof inviteSchema>

export function InviteUserPage() {
  const { account, user } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
    },
  })

  // Fetch existing invitations for this account
  const { data: invitations, isLoading } = useQuery({
    queryKey: ['invitations', account?.id],
    queryFn: async () => {
      if (!account?.id) return []
      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .eq('account_id', account.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Invitation[]
    },
    enabled: !!account?.id,
  })

  const inviteMutation = useMutation({
    mutationFn: async (data: InviteFormData) => {
      if (!account?.id) throw new Error('No account found')

      // Generate invitation token
      const { data: tokenData, error: tokenError } = await supabase.rpc(
        'generate_invitation_token'
      )
      if (tokenError) throw tokenError

      // Create invitation
      const { data: invitation, error } = await supabase
        .from('invitations')
        .insert({
          email: data.email.trim().toLowerCase(),
          role: 'user',
          account_id: account.id,
          invited_by: user?.id,
          token: tokenData,
          expires_at: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000
          ).toISOString(),
        })
        .select('id')
        .single()
      if (error) throw error

      // Send the invitation email (recipient/token are read from the DB row)
      const { error: emailError } = await supabase.functions.invoke(
        'send-notification-email',
        { body: { type: 'invite', invitation_id: invitation.id } }
      )

      return { token: tokenData, emailSent: !emailError }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      form.reset()
      if (data.emailSent) {
        toast({
          title: 'Invitation sent',
          description: 'An invitation email has been sent.',
        })
      } else {
        toast({
          title: 'Invitation created',
          description:
            'The invitation email could not be sent — the link has been copied to your clipboard instead.',
          variant: 'destructive',
        })
        // Fall back to the copy-link flow so the invite can still be shared
        copyToClipboard(data.token)
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const copyToClipboard = (token: string) => {
    const link = `${window.location.origin}/accept-invite/${token}`
    navigator.clipboard.writeText(link)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
    toast({
      title: 'Link copied',
      description: 'Invitation link copied to clipboard.',
    })
  }

  const getInvitationStatus = (invitation: Invitation) => {
    if (invitation.accepted_at) return 'accepted'
    if (new Date(invitation.expires_at) < new Date()) return 'expired'
    return 'pending'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Invite Team Member</h1>
        <p className="text-muted-foreground">
          Invite colleagues to join {account?.name}
        </p>
      </div>

      <div className="grid gap-6">
        {/* Invite Form */}
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Send Invitation</CardTitle>
            <CardDescription>
              Enter the email address of the person you want to invite
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((data) => inviteMutation.mutate(data))}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="colleague@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        They will receive an invitation link valid for 7 days
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Send Invitation
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Invitations List */}
        <Card>
          <CardHeader>
            <CardTitle>Sent Invitations</CardTitle>
            <CardDescription>
              Track the status of your invitations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                        <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                      </TableRow>
                    ))
                  ) : invitations?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        No invitations sent yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations?.map((invitation) => {
                      const status = getInvitationStatus(invitation)
                      return (
                        <TableRow key={invitation.id}>
                          <TableCell className="font-medium">
                            {invitation.email}
                          </TableCell>
                          <TableCell>
                            <InvitationStatusBadge status={status} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(invitation.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(invitation.expires_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(invitation.token)}
                              >
                                {copiedToken === invitation.token ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function InvitationStatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
    pending: 'secondary',
    accepted: 'default',
    expired: 'destructive',
  }

  return (
    <Badge variant={variants[status] || 'secondary'}>
      {status}
    </Badge>
  )
}
